// ... existing code ...
import { useState, useEffect, useCallback } from 'react';
import { aliments as mockAliments, categoriesAliments } from '@/mocks/aliments';
import { supabase } from '@/lib/supabase';
import ImportAlimentsSQL from './ImportAlimentsSQL';

// ─── Types ─────────────────────────────────────────────────────────
interface DBAliment {
  id: number;
  nom: string;
  categorie: string;
  image: string | null;
  description: string;
  glucides_pour_100g: number;
  calories_100g: number;
  fibres_100g: number;
  index_glycemique: number;
  proteines_100g: number | null;
  lipides_100g: number | null;
  sucres_100g: number | null;
  charge_glycemique: number | null;
  nutriscore: string | null;
  aliases: string[] | null;
  source: string | null;
  score_confiance: number | null;
  created_at: string;
}

// SVG Icons
// ... existing icon components ...

// ... existing icons: AddIcon, ErrorIcon, CloseIcon, LoaderIcon, DatabaseIcon, ImageIcon, EditIcon, DeleteIcon, FileCodeIcon ...

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
);
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const LoaderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
);
const DatabaseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>
);
const ImageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
);
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
);
const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
);
const FileCodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 13 15 13"/><polyline points="9 17 15 17"/></svg>
);

const emptyForm = (): Omit<DBAliment, 'id' | 'created_at'> => ({
  nom: '',
  categorie: 'legumineuses',
  image: '',
  description: '',
  glucides_pour_100g: 0,
  calories_100g: 0,
  fibres_100g: 0,
  index_glycemique: 0,
  proteines_100g: null,
  lipides_100g: null,
  sucres_100g: null,
  charge_glycemique: null,
  nutriscore: null,
  aliases: null,
  source: null,
  score_confiance: null,
});

export default function AdminAliments() {
  const [customAliments, setCustomAliments] = useState<DBAliment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<DBAliment | null>(null);
  const [form, setForm] = useState<Omit<DBAliment, 'id' | 'created_at'>>(emptyForm());
  const [errors, setErrors] = useState<Partial<Record<string, string>>>();
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadAliments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: supaErr } = await supabase
        .from('aliments')
        .select('*')
        .order('id', { ascending: false });
      if (supaErr) throw supaErr;
      setCustomAliments(data ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(`Impossible de charger les aliments : ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAliments();
  }, [loadAliments]);

  const validate = (): boolean => {
    const e: Partial<Record<string, string>> = {};
    if (!form.nom.trim()) e.nom = 'Le nom est requis';
    if (!form.description.trim()) e.description = 'La description est requise';
    if (form.glucides_pour_100g < 0) e.glucides_pour_100g = 'Valeur invalide';
    if (form.calories_100g < 0) e.calories_100g = 'Valeur invalide';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      if (editTarget) {
        const { error: supaErr } = await supabase
          .from('aliments')
          .update(form)
          .eq('id', editTarget.id);
        if (supaErr) throw supaErr;
      } else {
        const { error: supaErr } = await supabase
          .from('aliments')
          .insert(form);
        if (supaErr) throw supaErr;
      }
      await loadAliments();
      setShowForm(false);
      setEditTarget(null);
      setForm(emptyForm());
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(`Erreur lors de la sauvegarde : ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (aliment: DBAliment) => {
    setEditTarget(aliment);
    setForm({ ...aliment });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const { error: supaErr } = await supabase.from('aliments').delete().eq('id', id);
      if (supaErr) throw supaErr;
      await loadAliments();
      setDeleteConfirm(null);
    } catch {
      setError('Erreur lors de la suppression.');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditTarget(null);
    setForm(emptyForm());
    setErrors({});
  };

  return (
    <div>
      {/* ─── Import SQL Upload ───────────────────────────────── */}
      <ImportAlimentsSQL />

      {/* ─── Header ────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Gestion des Aliments</h2>
          <p className="text-gray-500 text-sm mt-0.5">
            {mockAliments.length} de base + {customAliments.length} en base de données
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setEditTarget(null); setForm(emptyForm()); }}
            className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <AddIcon />
            Ajouter un aliment
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-5 text-sm">
          <ErrorIcon />
          {error}
          <button onClick={() => setError(null)} className="ml-auto cursor-pointer">
            <CloseIcon />
          </button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-5">
            {editTarget ? "Modifier l'aliment" : 'Nouvel aliment'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                value={form.nom}
                onChange={(e) => setForm({ ...form, nom: e.target.value })}
                placeholder="Ex: Lentilles corail"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
              {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
              <select
                value={form.categorie}
                onChange={(e) => setForm({ ...form, categorie: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white cursor-pointer"
              >
                {categoriesAliments.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">URL de l'image</label>
              <input
                type="url"
                value={form.image || ''}
                onChange={(e) => setForm({ ...form, image: e.target.value || null })}
                placeholder="https://..."
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description nutritionnelle de l'aliment..."
                rows={3}
                maxLength={500}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white resize-none"
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Glucides / 100g (g) *</label>
              <input
                type="number" min={0} max={100} step={0.1}
                value={form.glucides_pour_100g}
                onChange={(e) => setForm({ ...form, glucides_pour_100g: parseFloat(e.target.value) || 0 })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Calories / 100g (kcal) *</label>
              <input
                type="number" min={0} step={1}
                value={form.calories_100g}
                onChange={(e) => setForm({ ...form, calories_100g: parseInt(e.target.value) || 0 })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fibres / 100g (g)</label>
              <input
                type="number" min={0} max={100} step={0.1}
                value={form.fibres_100g}
                onChange={(e) => setForm({ ...form, fibres_100g: parseFloat(e.target.value) || 0 })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Index glycémique (0 = N/A)</label>
              <input
                type="number" min={0} max={100} step={1}
                value={form.index_glycemique}
                onChange={(e) => setForm({ ...form, index_glycemique: parseInt(e.target.value) || 0 })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            {/* Optional fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Protéines / 100g (g)</label>
              <input
                type="number" min={0} max={100} step={0.1}
                value={form.proteines_100g ?? ''}
                onChange={(e) => setForm({ ...form, proteines_100g: e.target.value ? parseFloat(e.target.value) : null })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lipides / 100g (g)</label>
              <input
                type="number" min={0} max={100} step={0.1}
                value={form.lipides_100g ?? ''}
                onChange={(e) => setForm({ ...form, lipides_100g: e.target.value ? parseFloat(e.target.value) : null })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sucres / 100g (g)</label>
              <input
                type="number" min={0} max={100} step={0.1}
                value={form.sucres_100g ?? ''}
                onChange={(e) => setForm({ ...form, sucres_100g: e.target.value ? parseFloat(e.target.value) : null })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Charge glycémique</label>
              <input
                type="number" min={0} step={1}
                value={form.charge_glycemique ?? ''}
                onChange={(e) => setForm({ ...form, charge_glycemique: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nutri-Score</label>
              <select
                value={form.nutriscore || ''}
                onChange={(e) => setForm({ ...form, nutriscore: e.target.value || null })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white cursor-pointer"
              >
                <option value="">--</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="E">E</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Score confiance (1-5)</label>
              <input
                type="number" min={1} max={5} step={1}
                value={form.score_confiance ?? ''}
                onChange={(e) => setForm({ ...form, score_confiance: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div className="md:col-span-2 flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-teal-500 hover:bg-teal-400 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {saving && <LoaderIcon />}
                {editTarget ? 'Enregistrer les modifications' : "Ajouter l'aliment"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <LoaderIcon />
          <span className="text-sm ml-3">Chargement depuis la base de données...</span>
        </div>
      )}

      {/* DB aliments list */}
      {!loading && customAliments.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2 text-teal-600">
            <DatabaseIcon />
            Aliments en base de données ({customAliments.length})
          </h3>
          <div className="space-y-3">
            {customAliments.map((aliment) => (
              <div key={aliment.id} className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-4">
                <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-gray-100 flex items-center justify-center">
                  {aliment.image ? (
                    <img src={aliment.image} alt={aliment.nom} className="w-full h-full object-cover object-top" />
                  ) : (
                    <ImageIcon />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{aliment.nom}</span>
                    <span className="bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded-full">
                      {categoriesAliments.find((c) => c.id === aliment.categorie)?.label ?? aliment.categorie}
                    </span>
                    {aliment.nutriscore && (
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                        Nutri-Score {aliment.nutriscore}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                    <span>{aliment.glucides_pour_100g}g G/100g</span>
                    <span>{aliment.calories_100g} kcal/100g</span>
                    <span>Fibres: {aliment.fibres_100g}g</span>
                    <span>IG: {aliment.index_glycemique || 'N/A'}</span>
                    {aliment.proteines_100g !== null && <span>Prot: {aliment.proteines_100g}g</span>}
                    {aliment.lipides_100g !== null && <span>Lip: {aliment.lipides_100g}g</span>}
                    {aliment.score_confiance !== null && (
                      <span>Confiance: {'★'.repeat(aliment.score_confiance)}{'☆'.repeat(5 - aliment.score_confiance)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleEdit(aliment)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <EditIcon />
                  </button>
                  {deleteConfirm === aliment.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(aliment.id)}
                        className="text-xs bg-red-500 text-white px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap"
                      >
                        Confirmer
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap"
                      >
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(aliment.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mock aliments (read-only) */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2 text-gray-400">
          <FileCodeIcon />
          Aliments intégrés ({mockAliments.length}) — lecture seule
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {mockAliments.map((aliment) => (
            <div key={aliment.id} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-gray-200">
                <img src={aliment.image} alt={aliment.nom} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{aliment.nom}</p>
                <p className="text-xs text-gray-500">{aliment.glucidesPour100g}g G/100g</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}